import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, CalendarDays, CalendarPlus, ShieldCheck, ShoppingBag, Tag, User, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/common/ui";
import { formatGBP } from "../components/common/StockBadge";
import AgeBadge from "../components/common/AgeBadge";
import StoreBranding from "../components/common/StoreBranding";
import api from "../lib/api";

const STATUS_LABEL = {
  RESERVATION_RECEIVED: "Reservation Received", UNDER_REVIEW: "Under Review", CONFIRMED: "Confirmed",
  READY_FOR_COLLECTION: "Ready for Collection", COLLECTED: "Collected", CANCELLED: "Cancelled",
};
const LEGAL_LABELS = {
  age_confirmed: "Confirmed aged 18 or over",
  terms_accepted: "Accepted Terms & Conditions",
  privacy_ack: "Acknowledged Privacy Policy",
  challenge25_ack: "Understands Challenge 25 / ID may be required",
};

export default function ReservationConfirmation() {
  const { number } = useParams();
  const [resv, setResv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get(`/me/reservations/${number}`).then(({ data }) => setResv(data)).catch(() => setResv(false)).finally(() => setLoading(false));
  }, [number]);

  const addToCalendar = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/me/reservations/${number}/calendar.ics`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${number}.ics`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch { toast.error("Could not generate calendar file."); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (!resv) return <div className="min-h-screen bg-ink grid place-items-center text-white/60" data-testid="reservation-not-found">Reservation not found.</div>;

  const discounts = resv.discounts || [];
  const legal = resv.legal || {};
  const timeline = resv.timeline || [];
  const isReceived = resv.status === "RESERVATION_RECEIVED";
  const isReady = resv.status === "READY_FOR_COLLECTION";

  return (
    <div className="min-h-screen bg-ink py-12" data-testid="reservation-confirmation">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        {isReady && (
          <div className="mb-8 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center" data-testid="ready-for-collection-banner">
            <div className="mx-auto h-12 w-12 grid place-items-center rounded-2xl bg-emerald-500/20 border border-emerald-500/40"><ShoppingBag className="h-6 w-6 text-emerald-300" /></div>
            <h2 className="mt-4 font-playfair text-2xl text-emerald-200">Your reservation is ready for collection</h2>
            <p className="mt-2 text-sm text-white/80">Collect on <strong className="text-white">{resv.collection_date}</strong> · {resv.collection_location_label}</p>
            <p className="mt-1 text-sm text-white/80">Estimated total payable in store: <strong className="text-white">{formatGBP(resv.estimated_total)}</strong></p>
            <p className="mt-3 text-xs text-emerald-200/80">Please bring valid photo ID — it may be required under Challenge 25.</p>
          </div>
        )}
        <div className="text-center">
          <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-emerald-500/10 border border-emerald-500/30"><CheckCircle2 className="h-8 w-8 text-emerald-400" /></div>
          <h1 className="mt-5 font-playfair text-3xl sm:text-4xl text-white">{isReceived ? "Reservation request received" : "Your reservation"}</h1>
          {isReceived && <p className="mt-2 text-white/60">Your reservation request has been received and is awaiting review.</p>}
          <p className="mt-4 inline-block rounded-full border border-gold/40 bg-gold/10 px-5 py-2 text-gold font-semibold tracking-wide" data-testid="reservation-number">{resv.reservation_number}</p>
        </div>

        {/* Collection + calendar */}
        <div className="mt-8 rounded-2xl border border-gold/30 bg-gold/5 p-6 space-y-3" data-testid="reservation-collection">
          {resv.collection_store?.name && (
            <div data-testid="reservation-store">
              <p className="text-[11px] uppercase tracking-widest text-gold/80 font-semibold">Your collection store</p>
              <StoreBranding store={resv.collection_store} showName showDirections showInstructions className="mt-2" />
            </div>
          )}
          <div className="flex items-center gap-2 text-white pt-1"><CalendarDays className="h-5 w-5 text-gold" /><span>Preferred collection date: <strong data-testid="reservation-date">{resv.collection_date}</strong></span></div>
          {!resv.collection_store?.name && <p className="text-sm text-gold">{resv.collection_location_label}</p>}
          <Button data-testid="add-to-calendar-btn" onClick={addToCalendar} disabled={downloading} variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 gap-2">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} Add Collection Date to Calendar
          </Button>
        </div>

        {/* Contact snapshot */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="reservation-contact">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><User className="h-5 w-5 text-gold" /> Contact details</h2>
          <div className="mt-3 text-sm text-white/70 space-y-1">
            <p>{resv.contact?.first_name} {resv.contact?.last_name}</p>
            <p>{resv.contact?.email}</p>
            <p>{resv.contact?.phone}</p>
          </div>
        </div>

        {/* Summary */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="reservation-summary">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-gold" /> Summary</h2>
          <div className="mt-3 space-y-2">
            {(resv.items || []).map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm">
                <span className="text-white/80 truncate">{it.qty} × {it.name}{it.sku ? <span className="text-white/40"> · {it.sku}</span> : null}</span>
                <span className="text-white ml-2 whitespace-nowrap">{formatGBP(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 border-t border-white/10 pt-3 flex justify-between text-white/80 text-sm"><span>Subtotal</span><span>{formatGBP(resv.subtotal)}</span></div>
          {discounts.map((d) => (
            <div key={d.promotion_id} className="flex justify-between text-sm text-emerald-400 mt-1"><span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" />{d.label}{d.is_voucher ? " (coupon)" : ""}</span><span>− {formatGBP(d.amount)}</span></div>
          ))}
          <div className="mt-3 border-t border-white/10 pt-3 flex justify-between text-white font-semibold"><span>Estimated total payable in store</span><span data-testid="reservation-total">{formatGBP(resv.estimated_total)}</span></div>
        </div>

        {/* Timeline */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="reservation-timeline">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><Clock className="h-5 w-5 text-gold" /> Status</h2>
          <div className="mt-3"><StatusBadge status={STATUS_LABEL[resv.status] || "Received"} /></div>
          <ol className="mt-4 space-y-3 border-l border-white/10 pl-4">
            {timeline.map((t, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-gold" />
                <p className="text-sm text-white">{STATUS_LABEL[t.status] || t.status}</p>
                <p className="text-xs text-white/40">{(t.at || "").replace("T", " ").slice(0, 16)}{t.note ? ` · ${t.note}` : ""}</p>
              </li>
            ))}
          </ol>
        </div>

        {/* Legal */}
        <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="reservation-legal">
          <h2 className="font-playfair text-lg text-white flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-gold" /> Confirmations</h2>
          <div className="mt-3 space-y-2">
            {Object.entries(LEGAL_LABELS).map(([k, label]) => (
              <div key={k} className="flex items-center gap-2 text-sm text-white/70">
                <CheckCircle2 className={`h-4 w-4 ${legal[k] ? "text-emerald-400" : "text-white/20"}`} /> {label}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4" data-testid="confirmation-age-notice">
            <AgeBadge size={40} showText={false} linked />
            <p className="text-xs text-white/50">Payment is made in store when you collect. No payment has been taken online. Please bring valid ID — it may be required under Challenge 25.</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/dashboard/reservations"><Button data-testid="view-reservations-btn" className="cf-gold-btn font-semibold">My reservations</Button></Link>
          <Link to="/contact"><Button variant="outline" className="border-white/20 text-white">Need help?</Button></Link>
          <Link to="/fireworks"><Button variant="outline" className="border-white/20 text-white">Continue browsing</Button></Link>
        </div>
      </div>
    </div>
  );
}
